import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getFullScan, getScanCheckedIds, startFullScan, processFullScan, type FullScanRun } from '@/lib/api';
import { readScanCache, rememberScannedIds } from '@/lib/scan-cache';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { formatTimestamp } from '@/lib/utils';

export function FullScanPanel() {
  const qc = useQueryClient();
  const latest = useQuery({ queryKey: ['full-scan'], queryFn: getFullScan });
  const run = latest.data?.run;
  const [running, setRunning] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remembered, setRemembered] = useState(0);
  const stop = useRef(false);
  const busy = useRef(false);
  const mounted = useRef(true);
  // Keep the id across a failed start response so Retry cannot start another run.
  const startRequest = useRef<{ id: string; excludeIds?: string[] } | null>(null);
  useEffect(() => {
    mounted.current = true;
    try { setRemembered(readScanCache(localStorage).length); }
    catch { setError('Browser scan history could not be read. Check that browser storage is enabled.'); }
    return () => { mounted.current = false; stop.current = true; };
  }, []);

  function update(next: FullScanRun) {
    qc.setQueryData(['full-scan'], { run: next });
    void qc.invalidateQueries({ queryKey: ['flags'] });
    void qc.invalidateQueries({ queryKey: ['flag-player'] });
  }

  function remember(ids: string[]) {
    try {
      const count = rememberScannedIds(localStorage, ids);
      if (mounted.current) setRemembered(count);
    } catch {
      throw new Error('Could not save scan history in this browser. Check browser storage and available space');
    }
  }

  async function recoverChecked(id: string) {
    let after = '';
    do {
      const page = await getScanCheckedIds(id, after);
      remember(page.ids);
      if (!page.next) break;
      after = page.next;
    } while (!stop.current);
  }

  async function scan(newOnly = false) {
    if (busy.current) return;
    busy.current = true;
    stop.current = false;
    setRunning(true); setPausing(false); setError(null);
    try {
      const current = await getFullScan();
      // Import the latest run, including batches whose response was lost.
      if (current.run) await recoverChecked(current.run.id);
      if (stop.current) return;
      let next: FullScanRun;
      if (current.run?.status === 'running') next = current.run;
      else {
        startRequest.current ??= { id: crypto.randomUUID(), excludeIds: newOnly ? readScanCache(localStorage) : undefined };
        next = (await startFullScan(startRequest.current.id, startRequest.current.excludeIds)).run;
        startRequest.current = null;
      }
      update(next);
      while (!stop.current && next.status === 'running') {
        const result = await processFullScan(next.id);
        next = result.run;
        update(next);
        remember(result.checkedIds ?? []);
      }
    } catch (e) {
      if (mounted.current) setError(e instanceof Error && e.message === 'scan_batch_busy'
        ? 'Another batch is still running. Wait a moment, then resume; progress is saved.'
        : `${e instanceof Error ? e.message : String(e)}. Progress is saved; resume to retry.`);
      void qc.invalidateQueries({ queryKey: ['full-scan'] });
    } finally {
      busy.current = false;
      if (mounted.current) { setRunning(false); setPausing(false); }
    }
  }

  return <section aria-label="Account scans" className="rounded-md border border-border p-4 space-y-3">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 className="font-semibold text-sm">Account scans</h2>
        <p className="text-xs text-muted-foreground mt-1">Check saved accounts and current-season leaderboard entries. Findings go to Flags and may hold leaderboard access. No automatic bans.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {run?.status === 'running' || running ? <Button size="sm" disabled={running || latest.isPending} onClick={() => void scan()}>
          {running ? 'Scanning…' : 'Resume scan'}
        </Button> : <>
          <Button size="sm" disabled={latest.isPending} onClick={() => void scan(true)}>Scan new accounts</Button>
          <Button size="sm" variant="secondary" disabled={latest.isPending} onClick={() => void scan()}>Full scan</Button>
        </>}
        {running && <Button size="sm" variant="secondary" disabled={pausing} onClick={() => { stop.current = true; setPausing(true); }}>
          {pausing ? 'Pausing…' : 'Pause'}
        </Button>}
      </div>
    </div>
    <p className="text-xs text-muted-foreground">{remembered.toLocaleString()} successfully checked accounts remembered in this browser. Scan new accounts skips these IDs; errors and accounts without a readable save can be retried. Full scan rechecks everyone. Clearing browser storage or switching browsers loses this history.</p>
    <p className="text-xs text-muted-foreground">Keep this page open to continue. You can pause or return later and resume. Developer accounts, active bans, and accounts without a backend save are reported as skipped. Unreadable saves are reported as errors.</p>
    {(error || latest.error) && <Alert variant="destructive"><AlertDescription>{error ?? String(latest.error)}</AlertDescription></Alert>}
    {run && <div className="space-y-2 text-xs">
      <div className="flex flex-wrap justify-between gap-2" role="status" aria-live="polite">
        <span>{run.status === 'completed' ? 'Scan complete' : running ? 'Scanning' : 'Ready to resume'}: {run.processed.toLocaleString()} / {run.total.toLocaleString()} accounts</span>
        <span className="text-muted-foreground">Started {formatTimestamp(run.started_at)}</span>
      </div>
      <progress aria-label="Accounts scanned" value={run.processed} max={Math.max(1, run.total)} className="w-full h-2 accent-primary" />
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <span>{run.clean.toLocaleString()} no findings</span>
        <Link className="text-blue-300 hover:underline" to="/flags?source=full_scan">{run.flagged.toLocaleString()} with findings</Link>
        <span>{run.skipped.toLocaleString()} skipped</span>
        <span>{run.errors.toLocaleString()} errors</span>
      </div>
      <p className="text-muted-foreground">No findings means the available data passed these checks, not proof of legitimate gameplay. Scores are not published by scanning. Error accounts can also have findings from data that was readable.</p>
      {run.issues.length > 0 && <details className="text-muted-foreground">
        <summary className="cursor-pointer">Skipped / incomplete accounts (up to 20, errors first)</summary>
        <ul className="mt-2 space-y-1">{run.issues.map(issue => <li key={issue.player_id} className="break-all">
          <Link className="text-blue-300 hover:underline" to={`/players/${encodeURIComponent(issue.player_id)}`}>{issue.player_id}</Link>: {issue.detail}
        </li>)}</ul>
      </details>}
    </div>}
  </section>;
}
